import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["10:00 AM", "11:00 AM", "12:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];
const getToday = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };

export default function BookingPage() {
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const u = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone || !date || !time) { toast.error("Pick a date, time, and fill your details."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/booking`, { ...f, date: date.toISOString().split("T")[0], time_slot: time });
      setDone(true); toast.success("Booked!"); window.scrollTo(0, 0);
    } catch { toast.error("Something went wrong. Try again."); }
    setSending(false);
  };

  if (done) return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-md">
        <CheckCircle className="w-10 h-10 text-white mx-auto" />
        <h1 className="text-2xl md:text-3xl tracking-tight mt-8">Call booked.</h1>
        <p className="text-zinc-400 text-xs mt-4 tracking-wider">
          {date?.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at {time}
        </p>
        <p className="text-zinc-500 text-xs mt-2 tracking-wider">We'll send a confirmation to your email.</p>
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
          <a href="https://wa.me/919041344420?text=Hi%20Prospera%2C%20I%20just%20booked%20a%20consultation.%20Confirming%20my%20slot." target="_blank" rel="noopener noreferrer" data-testid="booking-success-whatsapp"
            className="bg-white text-black px-8 py-4 text-xs tracking-[0.2em] hover:bg-zinc-200 transition-colors inline-flex items-center justify-center gap-3">
            Whatsapp us <ArrowRight className="w-4 h-4" />
          </a>
          <Link to="/" data-testid="booking-success-home"
            className="border border-white/20 px-8 py-4 text-xs tracking-[0.2em] hover:border-white/50 transition-colors text-center">
            Home
          </Link>
        </div>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white pt-28 pb-24">
      <div className="px-6 md:px-16 lg:px-28">
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] text-zinc-500 hover:text-white transition-colors mb-10 tracking-[0.2em]" data-testid="booking-back-link">
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-zinc-500 text-xs tracking-[0.3em] mb-6">Book a call</p>
          <h1 className="text-3xl md:text-4xl tracking-tight" data-testid="booking-page-title">
            Pick a time. Let's talk.
          </h1>
          <p className="text-zinc-500 text-xs mt-4 tracking-wider">30 minutes. Free. No pressure.</p>
        </motion.div>

        <form onSubmit={submit} className="mt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Left: Calendar + Time */}
            <div className="lg:col-span-5 space-y-10">
              <div>
                <p className="text-xs tracking-[0.2em] text-zinc-500 border-b border-white/10 pb-4 mb-6">Date</p>
                <div className="bg-[#080808] border border-white/10 p-5 inline-block" data-testid="booking-calendar">
                  <Calendar mode="single" selected={date} onSelect={setDate}
                    fromDate={getToday()}
                    disabled={[{ dayOfWeek: [0] }]}
                    className="text-white"
                    classNames={{
                      day_selected: "bg-white text-black",
                      day_today: "border border-white/40"
                    }} />
                </div>
              </div>
              <div>
                <p className="text-xs tracking-[0.2em] text-zinc-500 border-b border-white/10 pb-4 mb-6 flex items-center gap-2">
                  <Clock className="w-3 h-3" /> Time
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {TIMES.map(t => (
                    <button key={t} type="button" data-testid={`time-slot-${t.replace(/\s/g, "-")}`}
                      onClick={() => setTime(t)}
                      className={`text-[10px] tracking-wider px-3 py-3 border transition-colors ${time === t ? "bg-white text-black border-white" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
                      {t}
                    </button>
                  ))}
                </div>
                {date && time && (
                  <p className="text-[10px] text-zinc-400 mt-4 tracking-wider">
                    {date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} at {time}
                  </p>
                )}
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-7">
              <p className="text-xs tracking-[0.2em] text-zinc-500 border-b border-white/10 pb-4 mb-6">Your details</p>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Name *</label>
                    <Input data-testid="booking-name" value={f.name} onChange={e => u("name", e.target.value)}
                      placeholder="Full name" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Company</label>
                    <Input data-testid="booking-company" value={f.company} onChange={e => u("company", e.target.value)}
                      placeholder="Company name" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Email *</label>
                    <Input data-testid="booking-email" type="email" value={f.email} onChange={e => u("email", e.target.value)}
                      placeholder="you@email.com" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">Phone *</label>
                    <Input data-testid="booking-phone" value={f.phone} onChange={e => u("phone", e.target.value)}
                      placeholder="+91 XXXXX XXXXX" className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 h-11 rounded-none text-xs tracking-wider" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.15em] text-zinc-500 block mb-2">What do you want to discuss?</label>
                  <Textarea data-testid="booking-message" value={f.message} onChange={e => u("message", e.target.value)}
                    placeholder="Quick overview of what you need help with..." rows={4}
                    className="bg-transparent border-white/15 text-white placeholder:text-zinc-600 rounded-none text-xs tracking-wider resize-none" />
                </div>
                <Button type="submit" disabled={sending} data-testid="booking-submit-btn"
                  className="bg-white text-black hover:bg-zinc-200 rounded-none px-10 py-4 text-xs tracking-[0.2em] h-auto w-full disabled:opacity-50">
                  {sending ? "Booking..." : "Confirm booking"} {!sending && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
